//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { ICollectionResponse } from '@apis/collections/ICollectionResponse';
import {
  ContentLibrarySortType,
  ContentLibraryV2RequestBody,
  ManualModerationStatus,
} from '@apis/content-library/contentLibraryApiModel';
import { buildCollectionSourceFilters } from '@media-library/moderation/utils/buildCollectionSourceFilters';

export const MODERATION_GRID_CARD_HEIGHT = 364;

/**
 * Stream view: content a moderator can share to the brand. Already shared or
 * client-rejected items are excluded via a negated manualModerationStatus leaf,
 * scoped to the collection's membership and tracked sources.
 */
export const getModerationStreamFilterParams = (
  collection: ICollectionResponse | undefined,
): ContentLibraryV2RequestBody => ({
  isModeration: true,
  shouldHydrateManualModerationMetadata: true,
  query: {
    '@type': 'and',
    filters: [
      {
        '@type': 'manualModerationStatus',
        status: [
          ManualModerationStatus.Shared,
          ManualModerationStatus.ClientRejected,
        ],
        negated: true,
      },
      ...(collection
        ? [
            {
              '@type': 'or' as const,
              filters: buildCollectionSourceFilters(collection),
            },
          ]
        : []),
    ],
  },
});

/**
 * True for moderator list requests built by the helpers above, i.e. lists
 * whose items carry manualModeratorMediaMetadata.
 */
export const isManualModerationListRequest = (
  req: ContentLibraryV2RequestBody | undefined,
): req is ContentLibraryV2RequestBody =>
  req?.isModeration === true &&
  req.shouldHydrateManualModerationMetadata === true;

/**
 * All Shared Media spans every stream, scoped to the moderator's shared and
 * client-rejected content.
 */
export const getAllSharedMediaFilterParams =
  (): ContentLibraryV2RequestBody => ({
    isModeration: true,
    orderBy: ContentLibrarySortType.MANUAL_MODERATION_SHARE_TIMESTAMP,
    shouldHydrateManualModerationMetadata: true,
    query: {
      '@type': 'or',
      filters: [
        {
          '@type': 'manualModerationStatus',
          status: [ManualModerationStatus.Shared],
        },
        {
          '@type': 'manualModerationStatus',
          status: [ManualModerationStatus.ClientRejected],
        },
      ],
    },
  });
